import { Box, Button, Grid, Typography, styled } from '@mui/material'
import ApprovalApi from 'api/approval/approval.api'
import BtnEditUnderline from 'assets/button/BtnEditUnderline'
import ButtonCustom from 'component/Button/ButtonCustom'
import Loading from 'component/Loading'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import { colors } from 'constants/theme'
import MainButton from 'new-components/buttons/main-button'
import PageModal from 'new-components/modal/page-modal'
import { useRef, useState } from 'react'
import CanvasDraw from 'react-canvas-draw'
import { useTranslation } from 'react-i18next'
import { dateToView } from 'utils/date.utils'
import { numberFormat } from 'utils/app.utils'

const StyledSignatureButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  height: 100px;
  width: 200px;
  border: 1px dashed #bdbdbd;
  padding: 10px;
  color: #828282;
  background-color: transparent;
  &:hover {
    border-color: #808080;
    background-color: #f5f5f5;
  }
`;

export const SignBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    '> img': {
        position: 'absolute',
        top: 0,
    },
    '.canvas-box > div': {
        margin: 'auto',
        width: '100% !important',
        height: '300px !important',
        border: `1px solid ${colors.lightGray}`,
        borderRadius: '4px',
    },
    '.clear-box': {
        opacity: 1,
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 5,
        '.MuiButton-root': {
            padding: '0 12px',
            minWidth: 'unset',
            borderColor: `${colors.themeThirdColor} !important`,
            color: `${colors.themeThirdColor} !important`,
            backgroundColor: `${colors.white} !important`,
            '&:hover': {
                opacity: 1
            }
        }
    }
}));

type PopupFinanceAcknowledgeProps = {
    open: boolean
    onClose: () => void
    data: any
    onSuccess: () => void
    financeUserName: string
}

const PopupFinanceAcknowledge = (props: PopupFinanceAcknowledgeProps) => {
    const { t } = useTranslation()
    const { open, onClose, data, onSuccess, financeUserName } = props
    const [loading, setLoading] = useState<boolean>(false)
    const [financeSignature, setFinanceSignature] = useState<string>('')
    const [isOpenPageModal, setIsOpenPageModal] = useState(false)
    const [isCanvasDirty, setIsCanvasDirty] = useState(false)
    const canvasRef: any = useRef()

    // Parse body data
    const bodyData = data?.body ? JSON.parse(data.body) : {}

    const clearCanvas = () => {
        if (canvasRef.current) {
            canvasRef.current.clear()
            setIsCanvasDirty(false)
        }
    }

    const handleSignature = (type: "submit" | "clear") => {
        if (type === "submit") {
            if (canvasRef.current) {
                const base64 = canvasRef.current.canvasContainer.childNodes[1].toDataURL('image/jpg')
                let base64string: any = `${base64}`.split(',')
                base64string = base64string[base64string.length - 1]
                setFinanceSignature(base64string)
            }
        } else {
            setFinanceSignature('')
        }
        setIsOpenPageModal(false)
        setIsCanvasDirty(false)
    }

    const clearStampedSignature = () => {
        setFinanceSignature('')
    }

    const handleSubmit = async () => {
        if (!financeSignature) {
            notiError(t('กรุณาลงลายเซ็น'))
            return
        }

        setLoading(true)
        try {
            await ApprovalApi.financeAcknowledge(data.approvalItemId, {
                financeSignature
            })
            notiSuccess(t('บันทึกสำเร็จ ส่งคำขอไปยังผู้อนุมัติแล้ว'))
            onSuccess()
            onClose()
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || 'เกิดข้อผิดพลาด'
            notiError(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    if (!open) return null

    return (
        <>
            <Loading show={loading} />
            <ModalCustom
                title={t('รับทราบคำขอค้างชำระ')}
                modalStyle="modal-finance-acknowledge"
                closeButton
                size="lg"
                onClose={onClose}
                component={(
                    <Box padding="16px">
                        <Grid container spacing={2}>
                            {/* ข้อมูลคำขอ */}
                            <Grid item xs={12}>
                                <Box sx={{
                                    backgroundColor: "#F5F5F5",
                                    padding: "16px",
                                    borderRadius: "8px",
                                }}>
                                    <Typography fontWeight={600} marginBottom={2}>
                                        รายละเอียดคำขอค้างชำระ
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">ผู้ป่วย:</Typography>
                                            <Typography>{bodyData.firstname} {bodyData.lastname}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">CN:</Typography>
                                            <Typography>{bodyData.cnNumber || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">ยอดค้างชำระ:</Typography>
                                            <Typography fontWeight={600} color="primary">
                                                {numberFormat(bodyData.overdueTotal || 0, 2)} บาท
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">วันครบกำหนด:</Typography>
                                            <Typography>{bodyData.overdueDate ? dateToView(bodyData.overdueDate) : '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">แพทย์:</Typography>
                                            <Typography>{bodyData.doctorName || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">ผู้ขอ:</Typography>
                                            <Typography>{data.requestUserName || '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* ลายเซ็นที่มีอยู่ */}
                            <Grid item xs={12}>
                                <Typography fontWeight={600} marginBottom={2}>
                                    ลายเซ็นผู้เกี่ยวข้อง
                                </Typography>
                                <Grid container spacing={2} justifyContent="center">
                                    {/* ลายเซ็นผู้ป่วย */}
                                    <Grid item xs={6} md={4}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <Box sx={{
                                                border: "1px solid #E0E0E0",
                                                borderRadius: "6px",
                                                padding: "10px",
                                                height: "80px",
                                                width: "150px",
                                            }}>
                                                {bodyData.patientSignature && (
                                                    <img src={`data:image/png;base64,${bodyData.patientSignature}`} style={{ width: '130px', height: '60px', objectFit: 'contain' }} alt="" />
                                                )}
                                            </Box>
                                            <Typography variant="body2" sx={{ textAlign: "center", marginTop: "8px" }}>
                                                {bodyData.firstname} {bodyData.lastname}
                                            </Typography>
                                            <Typography variant="caption" sx={{ textAlign: "center", color: "#828282" }}>
                                                ผู้ป่วย
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* ลายเซ็นแพทย์ */}
                                    <Grid item xs={6} md={4}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <Box sx={{
                                                border: "1px solid #E0E0E0",
                                                borderRadius: "6px",
                                                padding: "10px",
                                                height: "80px",
                                                width: "150px",
                                            }}>
                                                {bodyData.doctorSignature && (
                                                    <img src={`data:image/png;base64,${bodyData.doctorSignature}`} style={{ width: '130px', height: '60px', objectFit: 'contain' }} alt="" />
                                                )}
                                            </Box>
                                            <Typography variant="body2" sx={{ textAlign: "center", marginTop: "8px" }}>
                                                {bodyData.doctorName || '-'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ textAlign: "center", color: "#828282" }}>
                                                แพทย์
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* ลายเซ็นพนักงานการเงิน */}
                            <Grid item xs={12}>
                                <Typography fontWeight={600} marginBottom={2}>
                                    ลายเซ็นพนักงานการเงิน (รับทราบ) <span style={{ color: colors.statusInactive }}>*</span>
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    {financeSignature ? (
                                        <Box sx={{ position: 'relative' }}>
                                            <Box sx={{
                                                border: "1px solid #E0E0E0",
                                                borderRadius: "6px",
                                                padding: "10px",
                                                height: "100px",
                                                width: "200px",
                                            }}>
                                                <Box display={'flex'} justifyContent={'center'}>
                                                    <img src={`data:image/png;base64,${financeSignature}`} style={{ width: '180px', height: '80px', objectFit: 'contain' }} alt="" />
                                                </Box>
                                            </Box>
                                            <Box sx={{ position: 'absolute', top: 5, right: 3, zIndex: 1 }}>
                                                <MainButton
                                                    variant={'outlined'}
                                                    title={t('ล้าง')}
                                                    type='danger'
                                                    onClick={clearStampedSignature}
                                                    style={{
                                                        fontSize: '12px',
                                                        minWidth: 'auto',
                                                        backgroundColor: 'white',
                                                        height: '25px'
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <StyledSignatureButton
                                            variant="outlined"
                                            onClick={() => setIsOpenPageModal(true)}
                                        >
                                            <BtnEditUnderline color={'#808080'} width='20' height='20' />
                                            <Typography sx={{ fontWeight: '500', fontSize: '16px', marginLeft: '8px' }}>
                                                คลิกเพื่อเซ็น
                                            </Typography>
                                        </StyledSignatureButton>
                                    )}
                                </Box>
                                <Typography variant="body2" sx={{ textAlign: "center", marginTop: "8px" }}>
                                    {financeUserName || '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ textAlign: "center", color: "#828282", display: 'block' }}>
                                    พนักงานการเงิน / Finance Staff
                                </Typography>
                            </Grid>

                            {/* ปุ่ม */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '16px' }}>
                                    <MainButton
                                        variant="outlined"
                                        type='secondary'
                                        title={t("BUTTON.CANCEL")}
                                        onClick={onClose}
                                        style={{ width: '100px' }}
                                    />
                                    <MainButton
                                        variant='contained'
                                        type='primary'
                                        title={t("บันทึกและส่งอนุมัติ")}
                                        onClick={handleSubmit}
                                        disabled={!financeSignature}
                                        style={{ width: '150px' }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            />

            {/* Modal สำหรับเซ็นลายเซ็น */}
            <PageModal
                isOpen={isOpenPageModal}
                size={'S'}
                title={t('ลงลายเซ็น')}
                handleClose={() => handleSignature("clear")}
                onSubmit={{ onClick: () => handleSignature("submit"), disabled: !isCanvasDirty }}
                onCancel={{ onClick: () => handleSignature("clear") }}
                overflow
            >
                <Box>
                    <Box className="w-100 mt-3 mt-md-0">
                        <SignBox>
                            <Box className="clear-box">
                                <ButtonCustom variant={'outlined'} textButton={t('ล้าง')} onClick={clearCanvas} />
                            </Box>
                            <Box className="canvas-box" onMouseDown={() => setIsCanvasDirty(true)} onTouchStart={() => setIsCanvasDirty(true)}>
                                <CanvasDraw
                                    ref={canvasRef}
                                    hideGrid
                                    hideInterface
                                    brushRadius={2}
                                    lazyRadius={2}
                                    brushColor={colors.black}
                                />
                            </Box>
                            <Box className='text-center mt-2'>
                                <Typography sx={{ fontWeight: 500 }}>{financeUserName || '-'}</Typography>
                                <Typography sx={{ fontSize: 14 }}>พนักงานการเงิน</Typography>
                                <Typography sx={{ fontSize: 14 }}>Finance Staff</Typography>
                            </Box>
                        </SignBox>
                    </Box>
                </Box>
            </PageModal>
        </>
    )
}

export default PopupFinanceAcknowledge
